/**
 * Calendar in the widget bar (#1823): the next appointments when Microsoft 365
 * is connected, otherwise the month with a hint to connect.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'lucide-react'
import { logError } from '@/lib/logger'
import {
  buildAgenda,
  fetchUpcomingEvents,
  monthGrid,
  type AgendaItem,
  type UpcomingEvents,
} from '@/services/calendar-widget-service'
import type { WidgetProps } from '@/types/widget'
import { WIDGET_TILE_CLASS } from './tile'

const REFRESH_MS = 5 * 60 * 1000
const TICK_MS = 30 * 1000
const ACTION_CLASS =
  'h-9 rounded-full border border-[var(--window-border-color)] px-3.5 text-[13px] text-[var(--text-color)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'

function MonthGrid({ now, size, font, locale }: { now: Date; size: number; font: number; locale: string }) {
  const { t } = useTranslation()
  const monday = new Date(2024, 0, 1)
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Date(monday.getFullYear(), 0, 1 + index).toLocaleDateString(locale, { weekday: 'short' }).slice(0, 2),
  )
  const label = now.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
  return (
    <div
      role="img"
      aria-label={t('widgetBar.calendar.monthLabel', { month: label, day: now.getDate() })}
      className="grid flex-none grid-cols-7 items-center justify-items-center"
      style={{ gridAutoRows: size + 1, fontSize: font, width: (size + 6) * 7 }}
    >
      {weekdays.map((weekday) => (
        <span key={weekday} className="font-medium text-[var(--text-secondary)]">{weekday}</span>
      ))}
      {monthGrid(now).map((cell, index) => (
        <span
          key={index}
          className={`flex items-center justify-center rounded-full tabular-nums ${
            cell.today
              ? 'bg-[var(--theme-interactive-color)] font-bold text-[var(--theme-interactive-foreground)]'
              : 'text-[var(--text-color)]'
          }`}
          style={{ width: size, height: size }}
        >
          {cell.day}
        </span>
      ))}
    </div>
  )
}

function EventRow({ item, time, accent }: { item: AgendaItem; time: string; accent: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`block h-[30px] w-[3px] flex-none rounded-sm ${
          accent ? 'bg-[var(--theme-interactive-color)]' : 'bg-[var(--window-border-color)]'
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-[var(--text-color)]">{item.event.subject}</div>
        <div className={`truncate text-xs ${accent ? 'font-medium text-[var(--theme-interactive-color)]' : 'text-[var(--text-secondary)]'}`}>
          {time}
        </div>
      </div>
    </div>
  )
}

export function CalendarBarWidget({ config, onOpenApp, staticPreview }: WidgetProps) {
  const { t, i18n } = useTranslation()
  const [now, setNow] = useState(() => new Date())
  const [data, setData] = useState<UpcomingEvents | null>(null)
  const [failed, setFailed] = useState(false)
  const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB'
  const isSmall = config.size === 'small'

  const load = useCallback(async () => {
    try {
      setData(await fetchUpcomingEvents(new Date()))
      setFailed(false)
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'CalendarBarWidget.load' })
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    if (staticPreview) return
    void load()
    const refresh = setInterval(() => void load(), REFRESH_MS)
    const tick = setInterval(() => setNow(new Date()), TICK_MS)
    return () => {
      clearInterval(refresh)
      clearInterval(tick)
    }
  }, [load, staticPreview])

  const clock = (value: string | null) =>
    value ? new Date(value).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : ''

  const meta = ({ event, running, minutesLeft }: AgendaItem) => {
    const place = event.online_meeting ? 'Teams' : event.location
    const when = event.all_day
      ? t('widgetBar.calendar.allDay')
      : running
        ? t('widgetBar.calendar.now', { minutes: minutesLeft })
        : `${clock(event.start)} – ${clock(event.end)}`
    return place ? `${when} · ${place}` : when
  }

  const openConnections = () => onOpenApp('communicator2', { view: 'settings', connectorArea: 'calendar' })

  const notice = failed
    ? { text: t('widgetBar.calendar.failed'), action: t('widgetBar.calendar.retry'), run: () => void load() }
    : data?.state === 'not_connected'
      ? { text: t('widgetBar.calendar.connectHint'), action: t('widgetBar.calendar.connect'), run: openConnections }
      : data?.state === 'expired'
        ? { text: t('widgetBar.calendar.expired'), action: t('widgetBar.calendar.renew'), run: openConnections }
        : null

  const showAgenda = data?.state === 'connected' && !failed
  const agenda = showAgenda ? buildAgenda(data.events, now) : null
  const label = t('widgetBar.names.calendar')

  if (isSmall) {
    const next = agenda?.today[0]
    return (
      <section aria-label={label} className={`${WIDGET_TILE_CLASS} flex-col ${agenda ? 'justify-between p-3.5' : 'gap-1 px-3 py-2.5'}`}>
        {agenda ? (
          next ? (
            <>
              <span className="text-xs font-medium text-[var(--text-secondary)]">{t('widgetBar.calendar.next')}</span>
              <div>
                <div className="text-[28px] font-black leading-none tabular-nums">{clock(next.event.start)}</div>
                <div className="mt-2 line-clamp-2 text-[13px] font-medium leading-snug">{next.event.subject}</div>
              </div>
            </>
          ) : (
            <>
              <span className="text-xs font-medium text-[var(--text-secondary)]">{t('widgetBar.calendar.title')}</span>
              <div className="text-sm font-bold leading-snug">{t('widgetBar.calendar.free')}</div>
            </>
          )
        ) : (
          <>
            <span className="text-xs font-medium text-[var(--text-secondary)]">
              {now.toLocaleDateString(locale, { month: 'long' })}
            </span>
            <MonthGrid now={now} size={14} font={9} locale={locale} />
          </>
        )}
      </section>
    )
  }

  if (agenda) {
    const header = (
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-medium text-[var(--text-secondary)]">
          <Calendar className="h-4 w-4" aria-hidden />
          {t('widgetBar.calendar.title')}
        </span>
        <span className="text-xs text-[var(--text-secondary)]">
          {now.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
      </div>
    )
    return (
      <section aria-label={label} className={`${WIDGET_TILE_CLASS} flex-col gap-2.5 p-4`}>
        {header}
        {agenda.today.length > 0 ? (
          <div className="flex flex-col gap-2">
            {agenda.today.slice(0, 3).map((item, index) => (
              <EventRow key={`${item.event.subject}-${item.event.start}-${index}`} item={item} time={meta(item)} accent={index === 0} />
            ))}
          </div>
        ) : (
          <div className="flex flex-1 flex-col justify-center gap-3">
            <div className="text-base font-bold">{t('widgetBar.calendar.free')}</div>
            {agenda.tomorrow && (
              <div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {t('widgetBar.calendar.tomorrow', { time: clock(agenda.tomorrow.start) })}
                </div>
                <div className="mt-0.5 truncate text-sm font-medium">{agenda.tomorrow.subject}</div>
              </div>
            )}
          </div>
        )}
      </section>
    )
  }

  return (
    <section aria-label={label} className={`${WIDGET_TILE_CLASS} items-center gap-4 p-4`}>
      <MonthGrid now={now} size={18} font={11} locale={locale} />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <div>
          <div className="text-[15px] font-bold">{now.toLocaleDateString(locale, { weekday: 'long' })}</div>
          <div className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            {now.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}
          </div>
        </div>
        {notice && (
          <>
            <span className="text-xs leading-snug text-[var(--text-secondary)]">{notice.text}</span>
            <button type="button" onClick={notice.run} className={ACTION_CLASS}>
              {notice.action}
            </button>
          </>
        )}
      </div>
    </section>
  )
}
